import { Spinner } from "@blueprintjs/core";
import * as React from "react";
import { useParams } from "react-router";

import { BroodRequestApi, BroodRequestDto, EPlatform, ERequestState, RequestDto } from "../../../../api";
import { PageContainer } from "../../../../components";
import { useAuth } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalPuppyGenealogy } from "./DrupalPuppyGenealogy";
import { DrupalRequestResume, DrupalStepper } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { DrupalBroodRequestForm } from "./DrupalBroodRequestForm";
import { isBrcs } from "../../../DrupalApp";

export interface IDrupalHealthTestRequestPageProps {}

export const DrupalBroodRequestPage: React.FunctionComponent<IDrupalHealthTestRequestPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const broodRequestId = React.useMemo(() => +id, [id]);
  const { t } = useTl();
  const api = useApiService(BroodRequestApi);
  const { user } = useAuth();
  const [adnProfile, setAdnProfile] = React.useState(null);
  const [pedigreeFile, setPedigreeFile] = React.useState(null);
  const [puppyPictures, setpuppyPictures] = React.useState([]);
  const [fatherAdnProfile, setFatherAdnProfile] = React.useState(null);
  const [fatherPedigreeFile, setFatherPedigreeFile] = React.useState(null);
  const [fatherId, setFatherId] = React.useState(null);
  const [motherId, setMotherId] = React.useState(null);

  const [currentStep, setCurrentStep] = React.useState(0);

  const { data, loading, saveItem, saving, refresh } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: async (apiId = broodRequestId) => {
          var res;
          apiId > 0
            ? (res = await api.getWithPuppies(apiId))
            : (res = BroodRequestDto.fromJS({
                potentialFatherIds: [],
                isBrcsRequest: isBrcs,
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                  platform: EPlatform.PORTAL,
                }),
              }).toJSON());
          return res;
        },
        saveApiFn: async (d: BroodRequestDto) => {
          console.log("saving...");
          var brId = 0;
          try {
            brId = await api.save(
              BroodRequestDto.fromJS({
                ...d,
                userId: user.userId,
                request: RequestDto.fromJS({
                  ...d.request,
                  platform: EPlatform.PORTAL,
                }),
              })
            );
            console.log("broodRequestId", brId);
            await api.saveDrupalFiles(
              d.motherId,
              brId,
              adnProfile ? { data: adnProfile.file, fileName: adnProfile.filename } : null,
              pedigreeFile ? { data: pedigreeFile.file, fileName: pedigreeFile.filename } : null,
              fatherId,
              fatherAdnProfile
                ? {
                    data: fatherAdnProfile.file,
                    fileName: fatherAdnProfile.filename,
                  }
                : null,
              fatherPedigreeFile
                ? {
                    data: fatherPedigreeFile.file,
                    fileName: fatherPedigreeFile.filename,
                  }
                : null,
              puppyPictures
                ? puppyPictures.map((pp) => {
                    return { data: pp.file, fileName: pp.filename };
                  })
                : []
            );
          } catch (e) {
            console.log("e", e);
            setCurrentStep(0);
            throw e;
          }
          return brId;
        },
        onSavedRoute: (d: any) =>
          d.request.status === ERequestState.BILLED
            ? `${EDrupalRoutes.broodRequest}/${d.broodRequestId}`
            : EDrupalRoutes.requestList,
        deleteApiFn: () => {},
      }),
      [
        adnProfile,
        api,
        broodRequestId,
        fatherAdnProfile,
        fatherId,
        fatherPedigreeFile,
        pedigreeFile,
        puppyPictures,
        user.userId,
      ]
    ),
    [broodRequestId]
  );

  React.useEffect(() => {
    if (data?.request?.status && data?.request?.status === ERequestState.EXECUTED) {
      setCurrentStep(2);
    }
  }, [data]);

  const stepDescription = React.useMemo(() => {
    switch (currentStep) {
      case 0:
        return t(ETLCodes.RequestInformation);
      case 1:
        return t(ETLCodes.RequestConfirmation);
    }
  }, [currentStep, t]);

  return loading ? (
    <Spinner />
  ) : (
    <PageContainer>
      <DrupalFieldSet
        title={
          currentStep === 2
            ? `${t(ETLCodes.Payment)}: ${t(ETLCodes.BroodAndBirthRequest)}`
            : data.request.status === ERequestState.GESTATION && !data.birthDate
            ? t(ETLCodes.BirthInformations)
            : `${t(ETLCodes.NewRequest)}: ${t(ETLCodes.BroodAndBirthRequest)}`
        }
      >
        {data?.request?.status === ERequestState.EXECUTED ||
        data?.request?.status === ERequestState.EXECUTED_AWAITING_DOCUMENTS ? null : (
          <DrupalStepper stepNumber={currentStep} />
        )}

        <div>
          <div style={{ textAlign: "left", padding: "0em 2em" }}>{stepDescription}</div>
          {currentStep <= 1 && data?.request?.status === ERequestState.REQUESTED ? (
            <DrupalBroodRequestForm
              broodRequestId={broodRequestId}
              stepNumber={currentStep}
              initialdata={data}
              loading={saving}
              onStepChange={(newStep) => setCurrentStep(newStep)}
              save={(formikdata) => saveItem(formikdata)}
              refresh={refresh}
              setAdnProfile={setAdnProfile}
              setPedigreeFile={setPedigreeFile}
              setFatherAdnProfile={setFatherAdnProfile}
              setFatherPedigreeFile={setFatherPedigreeFile}
              fatherId={fatherId}
              setFatherId={setFatherId}
              motherId={motherId}
              setMotherId={setMotherId}
              setpuppyPictures={setpuppyPictures}
            />
          ) : saving ? (
            <Spinner />
          ) : (
            data && (
              <DrupalRequestResume
                requestForm={
                  <DrupalBroodRequestForm
                    showStepper={data.request.status !== ERequestState.BILLED}
                    broodRequestId={broodRequestId}
                    stepNumber={currentStep}
                    initialdata={data}
                    loading={saving}
                    onStepChange={(newStep) => setCurrentStep(newStep)}
                    save={(formikdata) => saveItem(formikdata)}
                    refresh={refresh}
                    setAdnProfile={setAdnProfile}
                    setPedigreeFile={setPedigreeFile}
                    setFatherAdnProfile={setFatherAdnProfile}
                    setFatherPedigreeFile={setFatherPedigreeFile}
                    fatherId={fatherId}
                    setFatherId={setFatherId}
                    motherId={motherId}
                    setMotherId={setMotherId}
                    setpuppyPictures={setpuppyPictures}
                  />
                }
                requestDto={data?.request}
                bill={null}
              />
            )
          )}
        </div>
      </DrupalFieldSet>
      {broodRequestId > 0 ? (
        <DrupalFieldSet title={t(ETLCodes.Genealogy)}>
          <DrupalPuppyGenealogy broodRequestId={+broodRequestId} />
        </DrupalFieldSet>
      ) : null}
    </PageContainer>
  );
};
